// @ts-nocheck

// App
import React from 'react';
import Head from 'react-helmet';
import { useSelector } from 'react-redux';

// Hooks
import useAreaHead from './useAreaHead';
import useAreaStructuredData from './useAreaStructuredData';

// Misc / utils
import { areaUtils_getSrpMaxPageLimit } from 'app/shared/utils/areaUtils';
import routeUtils from 'app/shared/utils/routeUtils';
import VALUE_CONSTANTS from 'app/shared/constants/ValueConstants';

const AreaHead = () => {
  // Redux - Area
  const area = useSelector((state) => state.area.area);
  const facts = useSelector((state) => state.area.facts);
  const factsByBeds = useSelector((state) => state.area.factsByBeds);
  const minPrice = useSelector((state) => state.area.areaMinPrice);
  const maxPrice = useSelector((state) => state.area.areaMaxPrice);

  // Redux - path & query
  const pathname = useSelector((state) => state.location.current.pathname);
  const query = useSelector((state) => state.location.current.query);

  // Redux - listing
  const listings = useSelector((state) => state.listings.listingGroups.byCoords);
  const totalListings = useSelector((state) => state.listings.totalListings);
  const maxBuildings = useSelector((state) => state.listings.totalBuildings);

  // Redux - misc
  const filter = useSelector((state) => state.filter);
  const seoLinks = useSelector((state) => state.seo.seoFooterLinks.groups);

  const currentPage = query.page ? query.page : 1;
  const maxPage = Math.max(Math.ceil(maxBuildings / VALUE_CONSTANTS.MAX_LISTINGS_PER_PAGE), 1);

  // Hooks
  const {
    getHeaderDetails,
    getNextUrl,
    getPrevUrl,
    getCanonicalUrl,
    shouldAreaHeadNoIndex,
    shouldAreaHeadNoIndexNoFollow,
  } = useAreaHead();
  const { getAreaSchema, getAreaFaqPageSchema, getAreaFaqSchemaV2, getEventSchema } = useAreaStructuredData();

  // HP-7829: lowercase the URL at the source so both og:url and the canonical
  // link converge on the same lowercase form for case-variant requests.
  const url = ('https://hotpads.com' + pathname).toLowerCase();
  const areaPriceRange = { minPrice, maxPrice };
  const headerDetails = getHeaderDetails({
    area,
    areaPriceRange,
    searchSlug: filter.search.slug,
    filterDescription: filter.search.title,
    currentPage,
    listings,
    pathname,
    totalListings,
  });
  const { pageTitle, pageDescription, searchSlug } = headerDetails;
  const links = [];

  // Preload the first listing's hero image so it starts fetching during HTML parsing,
  // before the browser encounters the <img> tag behind 30+ JS chunks in the waterfall.
  const firstListing = listings?.[0];
  const firstListingImage = firstListing?.medPhotoUrls?.[0] || firstListing?.previewPhotoMed?.url;
  if (firstListingImage) {
    links.push({
      rel: 'preload',
      as: 'image',
      href: firstListingImage,
      media: '(max-width: 767px)',
      fetchpriority: 'high',
    });
  }
  const metas = [
    { name: 'description', content: pageDescription },
    { property: 'og:title', content: pageTitle },
    { property: 'og:type', content: 'website' },
    { property: 'og:image', content: 'http://filenet.hotpads.com/images/logos/logo_square.png' },
    { property: 'og:url', content: url },
    { property: 'og:description', content: pageDescription },
    { property: 'og:site_name', content: 'HotPads' },
    { property: 'og:locale', content: 'en_US' },
  ];
  const noIndexNoFollow = shouldAreaHeadNoIndexNoFollow({
    resourceId: area.resourceId,
    areaType: area.type,
  });
  const noIndex = shouldAreaHeadNoIndex({
    page: currentPage,
    maxPage,
  });

  if (noIndexNoFollow) {
    metas.push({ name: 'robots', content: 'noindex, nofollow' });
  } else if (noIndex) {
    metas.push({ name: 'robots', content: 'noindex' });
  }

  const maxPageLimit = areaUtils_getSrpMaxPageLimit(maxPage, searchSlug);
  const nextUrl = getNextUrl({
    pathname,
    page: currentPage,
    maxPage: maxPageLimit,
    query,
    areaState: area.state,
  });
  if (nextUrl) {
    links.push({ rel: 'next', href: nextUrl });
  }
  const prevUrl = getPrevUrl({ pathname, page: currentPage, query, areaState: area.state });
  if (prevUrl) {
    links.push({ rel: 'prev', href: prevUrl });
  }

  const canonicalUrl = getCanonicalUrl({
    areaState: area.state,
    areaType: area.type,
    page: currentPage,
    pathname,
    query,
    url,
  });
  if (canonicalUrl && !(noIndex || noIndexNoFollow)) {
    links.push({ rel: 'canonical', href: canonicalUrl });
  }

  const isHouse = filter.search && filter.search.slug === 'houses-for-rent';
  const houseOrApartment = isHouse ? 'SingleFamilyResidence' : 'ApartmentComplex';
  const schemaCategories = [];
  schemaCategories.push(
    getAreaSchema({
      area,
      houseOrApartment,
      listings,
      pathname,
      searchSlug,
      seoLinks,
    }),
  );

  if (!routeUtils.isNearMeUrl(pathname)) {
    if (area.id === '1543325321' && searchSlug === 'apartments-for-rent') {
      schemaCategories.push(getAreaFaqSchemaV2());
    } else {
      schemaCategories.push(
        getAreaFaqPageSchema({
          area,
          facts,
          factsByBeds,
          searchSlug,
          totalListings,
        }),
      );
    }
  }

  // Have to loop over 3D listings and push them individually into schema categories
  listings.forEach((listing) => {
    if (listing.has3dTour) {
      schemaCategories.push(getEventSchema({ listing }));
    }
  });

  return (
    <Head
      link={links}
      meta={metas}
      title={pageTitle}
      script={[
        {
          type: 'application/ld+json',
          innerHTML: JSON.stringify({
            '@context': 'http://schema.org',
            '@graph': schemaCategories,
          }),
        },
      ]}
    />
  );
};

export default AreaHead;
